import React, { useState, useEffect, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  FileText, Eye, Send, CheckCircle, XCircle,
  ChevronRight, ChevronLeft, Check, Loader2,
  Home, Calculator, AlertCircle,
  FileDown, X
} from 'lucide-react';
import toast from 'react-hot-toast';
import budgetSheetService from '../services/budgetSheetService';
import surveyService from '../services/surveyService';
import api from '../services/api';

const STEPS = [
  { id: 1, name: 'Fichas', icon: FileText },
  { id: 2, name: 'Margem', icon: Calculator },
  { id: 3, name: 'Revisão', icon: Eye },
  { id: 4, name: 'Enviar', icon: Send },
  { id: 5, name: 'Confirmar', icon: CheckCircle }
];

const SurveyBudgetWizard = ({ survey, onClose, onComplete }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [sheets, setSheets] = useState([]);
  const [environments, setEnvironments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [managerMargin, setManagerMargin] = useState(0);
  const [managerMarginNotes, setManagerMarginNotes] = useState('');
  const [isSavingMargin, setIsSavingMargin] = useState(false);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [isApproving, setIsApproving] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [budgetApprovalData, setBudgetApprovalData] = useState({ approved: true, notes: '' });
  const [showApprovalModal, setShowApprovalModal] = useState(false);

  const queryClient = useQueryClient();

  useEffect(() => {
    const doLoad = async () => {
      try {
        setLoading(true);
        const [sheetsResponse, surveyResponse] = await Promise.all([
          budgetSheetService.getSurveyBudgetSheets(survey.id),
          surveyService.getEnvironments(survey.id)
        ]);

        if (sheetsResponse.success) {
          setSheets(sheetsResponse.data);
        }
        if (surveyResponse.success) {
          setEnvironments(surveyResponse.environments || []);
        }
      } catch (error) {
        console.error('Erro ao carregar dados:', error);
      } finally {
        setLoading(false);
      }

      try {
        const response = await api.get(`/surveys/${survey.id}`);
        const surveyData = response.data?.data || response.data || {};
        const marginVal = surveyData.manager_margin !== undefined 
          ? surveyData.manager_margin 
          : (surveyData.manager_margin_percentage !== undefined ? surveyData.manager_margin_percentage : 0);
        setManagerMargin(parseFloat(marginVal) || 0);
        if (surveyData.manager_margin_notes) {
          setManagerMarginNotes(surveyData.manager_margin_notes || '');
        }
      } catch (error) {
        console.error('Erro ao carregar margem:', error);
      }
    };

    doLoad();
  }, [survey?.id]);

  const handleSaveMargin = async () => {
    setIsSavingMargin(true);
    try {
      await api.put(`/surveys/${survey.id}/manager-margin`, {
        margin: managerMargin,
        notes: managerMarginNotes
      });
      toast.success('Margem salva com sucesso!');
    } catch (error) {
      toast.error('Erro ao salvar margem');
    } finally {
      setIsSavingMargin(false);
    }
  };

  const handleGeneratePDF = async () => {
    setIsGeneratingPDF(true);
    try {
      const response = await api.post(`/surveys/${survey.id}/generate-budget-pdf`, {}, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      setPdfUrl(url);
      toast.success('PDF gerado com sucesso!');
    } catch (error) {
      toast.error('Erro ao gerar PDF');
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  const handleApproveBudget = async () => {
    setIsApproving(true);
    try {
      await surveyService.approveBudget(survey.id, {
        approved: budgetApprovalData.approved,
        notes: budgetApprovalData.notes
      });
      toast.success(budgetApprovalData.approved ? 'Orçamento aprovado!' : 'Orçamento rejeitado!');
      queryClient.invalidateQueries(['surveys']);
      if (onComplete) onComplete();
      if (onClose) onClose();
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Erro ao processar aprovação');
    } finally {
      setIsApproving(false);
    }
  };

  const totals = useMemo(() => {
    let totalPrice = 0;

    sheets.forEach(sheet => {
      totalPrice += parseFloat(sheet.calculated_total) || parseFloat(sheet.final_price) || parseFloat(sheet.total_price) || 0;
    });

    const marginAdicional = totalPrice * (managerMargin / 100);

    return {
      totalPrice,
      marginAdicional,
      finalPrice: totalPrice + marginAdicional
    };
  }, [sheets, managerMargin]);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0);
  };

  const sheetsByEnvironment = useMemo(() => {
    const grouped = {};
    environments.forEach(env => {
      const envSheets = sheets.filter(s => s.environment_id === env.id);
      if (envSheets.length > 0) {
        grouped[env.id] = { environment: env, sheets: envSheets };
      }
    });
    const sheetsWithoutEnv = sheets.filter(s => !s.environment_id);
    if (sheetsWithoutEnv.length > 0) {
      grouped['no-env'] = { environment: null, sheets: sheetsWithoutEnv };
    }
    return grouped;
  }, [sheets, environments]);

  const canProceedToNext = () => {
    switch (currentStep) {
      case 1:
        return sheets.length > 0;
      case 2:
        return true;
      case 3:
        return totals.finalPrice > 0;
      case 4:
        return pdfUrl !== null;
      case 5:
        return true;
      default:
        return true;
    }
  };

  const renderStepIndicator = () => (
    <div className="mb-8">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-semibold text-gray-900">
          Encerramento do Levantamento
        </h2>
        <span className="text-sm text-gray-500">
          Passo {currentStep} de {STEPS.length}
        </span>
      </div>

      <div className="flex items-center justify-between">
        {STEPS.map((step, index) => {
          const Icon = step.icon;
          const isActive = currentStep === step.id;
          const isCompleted = currentStep > step.id;

          return (
            <React.Fragment key={step.id}>
              <div className="flex flex-col items-center">
                <div
                  className={`w-10 h-10 rounded-full flex items-center justify-center transition-all ${
                    isCompleted
                      ? 'bg-[rgb(73,148,61)] text-white'
                      : isActive
                      ? 'bg-emerald-100 text-[rgb(73,148,61)] border-2 border-[rgb(73,148,61)]'
                      : 'bg-gray-200 text-gray-400'
                  }`}
                >
                  {isCompleted ? (
                    <Check className="w-5 h-5" />
                  ) : (
                    <Icon className="w-5 h-5" />
                  )}
                </div>
                <div className={`text-xs mt-1 ${isActive || isCompleted ? 'text-[rgb(73,148,61)] font-medium' : 'text-gray-400'}`}>
                  {step.name}
                </div>
              </div>

              {index < STEPS.length - 1 && (
                <div className={`flex-1 h-1 mx-2 rounded transition-all ${
                  currentStep > step.id ? 'bg-[rgb(73,148,61)]' : 'bg-gray-200'
                }`} />
              )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );

  const renderStep1_Fichas = () => (
    <div className="space-y-4">
      <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
        <p className="text-sm text-emerald-800">
          <strong>Revise as fichas de orçamento</strong> — Cada ambiente possui uma ou mais fichas que serão incluídas no orçamento final.
        </p>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-[rgb(73,148,61)]" />
        </div>
      ) : Object.keys(sheetsByEnvironment).length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <FileText className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500">Nenhuma ficha de orçamento cadastrada</p>
          <p className="text-sm text-gray-400 mt-1">
            Volte ao levantamento para adicionar fichas de orçamento
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {Object.entries(sheetsByEnvironment).map(([envId, { environment, sheets: envSheets }]) => (
            <div key={envId} className="border border-gray-200 rounded-lg overflow-hidden">
              <div className="px-4 py-3 bg-gray-50 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Home className="w-4 h-4 text-gray-500" />
                  <span className="font-medium text-gray-900">
                    {environment?.environment_name || 'Sem Ambiente'}
                  </span>
                </div>
                <span className="text-sm text-gray-500">
                  {envSheets.length} {envSheets.length === 1 ? 'ficha' : 'fichas'}
                </span>
              </div>

              <div className="divide-y divide-gray-100">
                {envSheets.map(sheet => (
                  <div key={sheet.id} className="px-4 py-3 flex items-center justify-between hover:bg-gray-50">
                    <div className="flex items-center gap-3">
                      <FileText className="w-5 h-5 text-[rgb(73,148,61)]" />
                      <div>
                        <p className="font-medium text-gray-900">
                          {sheet.model_name || 'Ficha de Orçamento'}
                        </p>
                        <p className="text-sm text-gray-500">
                          {sheet.model_type || 'Padrão'}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-[rgb(73,148,61)]">
                        {formatCurrency(sheet.calculated_total)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="bg-gray-100 rounded-lg p-4 mt-6">
        <div className="flex justify-between items-center">
          <span className="text-gray-600">Total das Fichas:</span>
          <span className="text-xl font-bold text-gray-900">{formatCurrency(totals.totalPrice)}</span>
        </div>
      </div>
    </div>
  );

  const renderStep2_Margem = () => (
    <div className="space-y-6">
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
        <p className="text-sm text-amber-800">
          <strong>Ajuste a margem de lucro</strong> — Adicione uma porcentagem extra ao valor total para cobrir custos administrativos e margem de lucro.
        </p>
      </div>

      <div className="max-w-md mx-auto">
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Margem adicional (%)
            </label>
            <div className="flex items-center gap-4">
              <input
                type="number"
                step="0.5"
                min="0"
                max="50"
                value={managerMargin}
                onChange={(e) => setManagerMargin(parseFloat(e.target.value) || 0)}
                className="flex-1 px-4 py-3 text-xl font-semibold border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-[rgb(73,148,61)] focus:border-[rgb(73,148,61)] text-center"
              />
              <span className="text-2xl text-gray-400">%</span>
            </div>
            <div className="flex justify-between text-xs text-gray-500 mt-2">
              <span>0%</span>
              <span>50%</span>
            </div>
          </div>

          <div className="border-t border-gray-200 pt-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Observações
            </label>
            <textarea
              rows={3}
              value={managerMarginNotes}
              onChange={(e) => setManagerMarginNotes(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[rgb(73,148,61)] focus:border-[rgb(73,148,61)]"
              placeholder="Ex: Margem para cobrir transporte, instalação..."
            />
          </div>

          <button
            onClick={handleSaveMargin}
            disabled={isSavingMargin}
            className="w-full mt-4 px-4 py-3 bg-[rgb(73,148,61)] text-white rounded-lg hover:bg-[rgb(60,120,50)] disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {isSavingMargin ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <Check className="w-5 h-5" />
            )}
            Salvar Margem
          </button>
        </div>
      </div>

      <div className="bg-gray-100 rounded-lg p-4">
        <div className="space-y-2">
          <div className="flex justify-between">
            <span className="text-gray-600">Subtotal:</span>
            <span className="font-medium">{formatCurrency(totals.totalPrice)}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">Margem ({managerMargin}%):</span>
            <span className="font-medium text-amber-600">+ {formatCurrency(totals.marginAdicional)}</span>
          </div>
          <div className="flex justify-between border-t border-gray-300 pt-2">
            <span className="font-semibold text-gray-900">Total:</span>
            <span className="font-bold text-xl text-[rgb(73,148,61)]">{formatCurrency(totals.finalPrice)}</span>
          </div>
        </div>
      </div>
    </div>
  );

  const renderStep3_Revisao = () => (
    <div className="space-y-4">
      <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
        <p className="text-sm text-emerald-800">
          <strong>Revise o orçamento final</strong> — Verifique todos os valores antes de enviar ao cliente.
        </p>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
          <h3 className="font-semibold text-gray-900">Resumo do Orçamento</h3>
          <p className="text-sm text-gray-500">Levantamento {survey.survey_code || survey.id}</p>
        </div>

        <div className="p-6 space-y-4">
          <div className="flex items-center justify-between py-3 border-b border-gray-100">
            <span className="text-gray-600">Valor de Venda</span>
            <span className="font-medium text-gray-900">{formatCurrency(totals.totalPrice)}</span>
          </div>

          <div className="flex items-center justify-between py-3 border-b border-gray-100">
            <span className="text-gray-600">Margem ({managerMargin}%)</span>
            <span className="font-medium text-amber-600">+ {formatCurrency(totals.marginAdicional)}</span>
          </div>

          <div className="flex items-center justify-between py-4 bg-emerald-50 -mx-6 px-6 rounded-lg">
            <span className="text-lg font-semibold text-gray-900">Valor Final</span>
            <span className="text-2xl font-bold text-[rgb(73,148,61)]">{formatCurrency(totals.finalPrice)}</span>
          </div>
        </div>
      </div>

      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
        <h4 className="font-medium text-gray-900 mb-2">Dados do Cliente</h4>
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <span className="text-gray-500">Nome:</span>
            <p className="font-medium">{survey.client_name}</p>
          </div>
          {survey.client_phone && (
            <div>
              <span className="text-gray-500">Telefone:</span>
              <p className="font-medium">{survey.client_phone}</p>
            </div>
          )}
        </div>
      </div>

      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
        <h4 className="font-medium text-gray-900 mb-2">Ambientes</h4>
        <div className="flex flex-wrap gap-2">
          {Object.entries(sheetsByEnvironment).map(([envId, { environment }]) => (
            <span
              key={envId}
              className="inline-flex items-center px-3 py-1 bg-white border border-gray-300 rounded-full text-sm"
            >
              <Home className="w-3 h-3 mr-1" />
              {environment?.environment_name || 'Sem ambiente'}
            </span>
          ))}
        </div>
      </div>

      {managerMarginNotes && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <h4 className="font-medium text-amber-900 mb-1">Observações sobre a margem</h4>
          <p className="text-sm text-amber-800">{managerMarginNotes}</p>
        </div>
      )}
    </div>
  );

  const renderStep4_Enviar = () => (
    <div className="space-y-4">
      <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
        <p className="text-sm text-emerald-800">
          <strong>Envie o orçamento ao cliente</strong> — Gere o PDF e envie manualmente por WhatsApp ou email.
        </p>
      </div>

      <div className="max-w-md mx-auto">
        <button
          onClick={handleGeneratePDF}
          disabled={isGeneratingPDF}
          className="w-full flex flex-col items-center justify-center p-8 bg-white border-2 border-gray-200 rounded-lg hover:border-[rgb(73,148,61)] hover:bg-emerald-50 transition-all disabled:opacity-50"
        >
          {isGeneratingPDF ? (
            <Loader2 className="w-16 h-16 text-[rgb(73,148,61)] animate-spin mb-3" />
          ) : (
            <FileDown className="w-16 h-16 text-[rgb(73,148,61)] mb-3" />
          )}
          <span className="font-semibold text-lg text-gray-900">Gerar PDF do Orçamento</span>
          <span className="text-sm text-gray-500 mt-1">Clique para baixar o documento</span>
        </button>
      </div>

      {pdfUrl && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <CheckCircle className="w-6 h-6 text-green-600" />
            <div>
              <p className="font-medium text-green-900">PDF gerado com sucesso!</p>
              <p className="text-sm text-green-700">Pronto para envio</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <a
              href={pdfUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="px-4 py-2 bg-gray-100 text-gray-800 rounded-lg hover:bg-gray-200 flex items-center gap-2 border border-gray-300 font-medium"
            >
              <Eye className="w-4 h-4 text-gray-600" />
              Visualizar
            </a>
            <a
              href={pdfUrl}
              download={`Orcamento-${survey.survey_code || survey.id}.pdf`}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2 font-medium shadow-sm"
            >
              <FileDown className="w-4 h-4" />
              Baixar PDF
            </a>
          </div>
        </div>
      )}

      <div className="bg-gray-100 rounded-lg p-4">
        <div className="flex justify-between items-center">
          <span className="text-gray-600">Valor do Orçamento:</span>
          <span className="text-xl font-bold text-[rgb(73,148,61)]">{formatCurrency(totals.finalPrice)}</span>
        </div>
      </div>

      <div className="text-center py-4">
        <p className="text-sm text-gray-500">
          Após enviar ao cliente, aguarde a resposta para confirmar a aprovação
        </p>
      </div>
    </div>
  );

  const renderStep5_Confirmar = () => (
    <div className="space-y-4">
      <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
        <p className="text-sm text-emerald-800">
          <strong>Confirme a resposta do cliente</strong> — Registre se o orçamento foi aprovado ou reprovado.
        </p>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h3 className="font-semibold text-gray-900 mb-4">Cliente aprovou o orçamento?</h3>

        <div className="grid grid-cols-2 gap-4 mb-6">
          <button
            onClick={() => {
              setBudgetApprovalData({ ...budgetApprovalData, approved: true });
              setShowApprovalModal(true);
            }}
            className="flex flex-col items-center justify-center p-6 border-2 border-gray-200 rounded-lg hover:border-green-500 hover:bg-green-50 transition-all"
          >
            <CheckCircle className="w-12 h-12 text-green-500 mb-2" />
            <span className="font-semibold text-gray-900">Sim, Aprovar</span>
            <span className="text-sm text-gray-500">Cliente aceitou o orçamento</span>
          </button>

          <button
            onClick={() => {
              setBudgetApprovalData({ ...budgetApprovalData, approved: false });
              setShowApprovalModal(true);
            }}
            className="flex flex-col items-center justify-center p-6 border-2 border-gray-200 rounded-lg hover:border-red-500 hover:bg-red-50 transition-all"
          >
            <XCircle className="w-12 h-12 text-red-500 mb-2" />
            <span className="font-semibold text-gray-900">Não, Reprovar</span>
            <span className="text-sm text-gray-500">Cliente recusou o orçamento</span>
          </button>
        </div>

        <div className="border-t border-gray-200 pt-4">
          <div className="flex justify-between items-center">
            <span className="text-gray-600">Valor aprovado:</span>
            <span className="text-xl font-bold text-[rgb(73,148,61)]">{formatCurrency(totals.finalPrice)}</span>
          </div>
        </div>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5" />
          <div className="text-sm text-blue-800">
            <p className="font-medium">O que acontece após a aprovação?</p>
            <p className="mt-1">Um projeto será criado automaticamente e você será notificado para dar início ao planejamento.</p>
          </div>
        </div>
      </div>
    </div>
  );

  const renderApprovalModal = () => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
        <div className="p-4 border-b">
          <h3 className="text-lg font-semibold">
            {budgetApprovalData.approved ? 'Confirmar Aprovação' : 'Confirmar Reprovação'}
          </h3>
          <p className="text-sm text-gray-500">
            {budgetApprovalData.approved
              ? 'O orçamento será aprovado e um projeto será criado.'
              : 'O orçamento será marcado como reprovado.'}
          </p>
        </div>

        <div className="p-4 space-y-3">
          <div>
            <label className="text-xs font-medium text-gray-700 block mb-1">Observações</label>
            <textarea
              rows={3}
              value={budgetApprovalData.notes}
              onChange={(e) => setBudgetApprovalData({ ...budgetApprovalData, notes: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              placeholder={budgetApprovalData.approved
                ? "Ex: Aprovado via WhatsApp em 10/08/2026"
                : "Ex: Cliente achou o valor muito alto"}
            />
          </div>
        </div>

        <div className="p-4 border-t flex justify-end gap-2">
          <button
            onClick={() => setShowApprovalModal(false)}
            className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
          >
            Cancelar
          </button>
          <button
            onClick={handleApproveBudget}
            disabled={isApproving}
            className={`px-4 py-2 text-white rounded-lg flex items-center gap-2 disabled:opacity-50 ${
              budgetApprovalData.approved
                ? 'bg-green-600 hover:bg-green-700'
                : 'bg-red-600 hover:bg-red-700'
            }`}
          >
            {isApproving && <Loader2 className="w-4 h-4 animate-spin" />}
            {budgetApprovalData.approved ? 'Confirmar Aprovação' : 'Confirmar Reprovação'}
          </button>
        </div>
      </div>
    </div>
  );

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 1: return renderStep1_Fichas();
      case 2: return renderStep2_Margem();
      case 3: return renderStep3_Revisao();
      case 4: return renderStep4_Enviar();
      case 5: return renderStep5_Confirmar();
      default: return null;
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[95vh] flex flex-col">
        <div className="p-6 border-b border-gray-200 flex-shrink-0 relative">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 p-1 rounded-lg hover:bg-gray-100"
            title="Fechar"
          >
            <X className="w-5 h-5" />
          </button>
          {renderStepIndicator()}
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {renderCurrentStep()}
        </div>

        <div className="p-6 border-t border-gray-200 flex-shrink-0">
          <div className="flex justify-between">
            <button
              onClick={() => setCurrentStep(Math.max(1, currentStep - 1))}
              disabled={currentStep === 1}
              className={`flex items-center gap-2 px-6 py-3 rounded-lg ${
                currentStep === 1
                  ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <ChevronLeft className="w-5 h-5" />
              Anterior
            </button>

            {currentStep < STEPS.length ? (
              <button
                onClick={() => setCurrentStep(currentStep + 1)}
                disabled={!canProceedToNext()}
                className="flex items-center gap-2 px-6 py-3 bg-[rgb(73,148,61)] text-white rounded-lg hover:bg-[rgb(60,120,50)] disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Próximo
                <ChevronRight className="w-5 h-5" />
              </button>
            ) : (
              <div className="text-sm text-gray-500 flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-500" />
                Processo concluído
              </div>
            )}
          </div>
        </div>

        {showApprovalModal && renderApprovalModal()}
      </div>
    </div>
  );
};

export default SurveyBudgetWizard;
